import type { BusinessSubscription, PlanPackage } from '@/types';

export type AnalyticsAccess = false | 'weekly' | 'advanced';

export interface PlanEntitlements {
  maxProducts: number;
  maxImagesPerProduct: number;
  maxAiConversationsPerMonth: number;
  bulkUpload: boolean;
  analytics: AnalyticsAccess;
  leadManagement: boolean;
  customDomain: boolean;
  staffAccounts: boolean;
  apiAccess: boolean;
}

const STARTER: PlanEntitlements = {
  maxProducts: 50,
  maxImagesPerProduct: 2,
  maxAiConversationsPerMonth: 10,
  bulkUpload: false,
  analytics: false,
  leadManagement: false,
  customDomain: false,
  staffAccounts: false,
  apiAccess: false,
};

const GROWTH: PlanEntitlements = {
  maxProducts: 100,
  maxImagesPerProduct: 5,
  maxAiConversationsPerMonth: 100,
  bulkUpload: true,
  analytics: 'weekly',
  leadManagement: false,
  customDomain: false,
  staffAccounts: false,
  apiAccess: false,
};

const PRO: PlanEntitlements = {
  maxProducts: 500,
  maxImagesPerProduct: 10,
  maxAiConversationsPerMonth: 500,
  bulkUpload: true,
  analytics: 'advanced',
  leadManagement: true,
  customDomain: true,
  staffAccounts: false,
  apiAccess: false,
};

const SCALE: PlanEntitlements = {
  maxProducts: Number.POSITIVE_INFINITY,
  maxImagesPerProduct: Number.POSITIVE_INFINITY,
  maxAiConversationsPerMonth: Number.POSITIVE_INFINITY,
  bulkUpload: true,
  analytics: 'advanced',
  leadManagement: true,
  customDomain: true,
  staffAccounts: true,
  apiAccess: true,
};

const BY_PACKAGE: Record<string, PlanEntitlements> = {
  freemium: STARTER,
  starter: STARTER,
  standard: GROWTH,
  growth: GROWTH,
  premium: PRO,
  pro: PRO,
  scale: SCALE,
};

function limitFromFeatures(
  features: string[],
  unlimitedPattern: RegExp,
  countPattern: RegExp
): number | undefined {
  for (const feature of features) {
    if (unlimitedPattern.test(feature)) return Number.POSITIVE_INFINITY;
    const match = feature.match(countPattern);
    if (match) return Number(match[1]);
  }
  return undefined;
}

function hasFeature(features: string[], pattern: RegExp) {
  return features.some((feature) => pattern.test(feature));
}

export function entitlementsForPackage(
  pkg?: PlanPackage | string | null
): PlanEntitlements {
  if (!pkg) return { ...STARTER };
  return { ...(BY_PACKAGE[pkg] ?? STARTER) };
}

export function resolvePlanEntitlements(
  subscription?: BusinessSubscription | null
): PlanEntitlements {
  const plan = subscription?.subscriptionPlan;
  const base = entitlementsForPackage(plan?.package);
  const features = plan?.features ?? [];

  const maxProducts = limitFromFeatures(
    features,
    /unlimited product uploads/i,
    /(\d+)\s+product uploads/i
  );
  const maxImages = limitFromFeatures(
    features,
    /unlimited images per product/i,
    /(\d+)\s+images per product/i
  );
  const maxAi = limitFromFeatures(
    features,
    /unlimited AI conversations/i,
    /(\d+)\s+AI conversations/i
  );

  let analytics = base.analytics;
  if (hasFeature(features, /advanced analytics/i)) analytics = 'advanced';
  else if (hasFeature(features, /weekly analytics/i)) analytics = 'weekly';

  const aiFromPlan = plan?.aiConversationLimit;

  return {
    ...base,
    ...(maxProducts !== undefined ? { maxProducts } : {}),
    ...(maxImages !== undefined ? { maxImagesPerProduct: maxImages } : {}),
    ...(maxAi !== undefined ? { maxAiConversationsPerMonth: maxAi } : {}),
    ...(typeof aiFromPlan === 'number'
      ? {
          maxAiConversationsPerMonth:
            aiFromPlan < 0 ? Number.POSITIVE_INFINITY : aiFromPlan,
        }
      : {}),
    ...(hasFeature(features, /bulk product upload/i)
      ? { bulkUpload: true }
      : {}),
    analytics,
    ...(hasFeature(features, /lead management/i)
      ? { leadManagement: true }
      : {}),
    ...(hasFeature(features, /custom domain/i) ? { customDomain: true } : {}),
    ...(hasFeature(features, /staff accounts/i) ? { staffAccounts: true } : {}),
    ...(hasFeature(features, /API access/i) ? { apiAccess: true } : {}),
  };
}

export function isWithinLimit(limit: number, nextCount: number) {
  return nextCount <= limit;
}
